"use client"

import { useEffect, useState } from "react"
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardImg,
    CardTitle,
    CardText,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Input,
    Label,
    FormFeedback,
    Spinner,
} from "reactstrap"
import "./Coach.css"
import { Breadcrumbs } from "../../../AbstractElements"
import { createCoach, deleteCoach, getAllCoaches, UpdateCoach } from "../../../api-handle/coachApi"

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"]

// Custom Button Component
const CustomButton = ({
    children,
    variant = "primary",
    size = "lg",
    disabled = false,
    loading = false,
    onClick,
    className = "",
    ...props
}) => {
    const baseClasses = "custom-btn"
    const variantClasses = {
        primary: "custom-btn-primary",
        secondary: "custom-btn-secondary",
        success: "custom-btn-success",
        danger: "custom-btn-danger",
        outline: "custom-btn-outline",
    }
    const sizeClasses = {
        sm: "custom-btn-sm",
        md: "custom-btn-md",
        lg: "custom-btn-lg",
    }

    return (
        <button
            className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} ${disabled ? "disabled" : ""}`}
            onClick={onClick}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <Spinner size="sm" className="me-2" />}
            {children}
        </button>
    )
}

const Coach = () => {
    const [coaches, setCoaches] = useState([])
    const [modalOpen, setModalOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [coachToDelete, setCoachToDelete] = useState(null)
    const [formState, setFormState] = useState({
        name: "",
        description: "",
        profilePhoto: null,
    })
    const [previewUrl, setPreviewUrl] = useState(null)
    const [editId, setEditId] = useState(null)
    const [errors, setErrors] = useState({})

    const File_BASE_URL = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/?$/, "/")

    // Fetch all coaches
    const fetchCoaches = async () => {
        try {
            setLoading(true)
            const res = await getAllCoaches()
            setCoaches(res?.coaches || [])
        } catch (error) {
            console.error("Failed to fetch coaches:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCoaches()
    }, [])

    // Toggle modal
    const toggleModal = () => {
        setModalOpen(!modalOpen)
        if (!modalOpen) resetForm()
    }

    // Toggle delete modal
    const toggleDeleteModal = () => {
        setDeleteModalOpen(!deleteModalOpen)
        if (!deleteModalOpen) {
            setCoachToDelete(null)
        }
    }

    // Reset form & errors
    const resetForm = () => {
        setFormState({ name: "", description: "", profilePhoto: null })
        setPreviewUrl(null)
        setEditId(null)
        setErrors({})
    }

    // Validate inputs
    const validate = () => {
        const errs = {}
        if (!formState.name.trim()) errs.name = "Name is required."

        if (!editId && !formState.profilePhoto) {
            errs.profilePhoto = "Profile photo is required."
        }

        if (formState.profilePhoto) {
            if (!ACCEPTED_IMAGE_TYPES.includes(formState.profilePhoto.type)) {
                errs.profilePhoto = "Only JPG and PNG files are allowed."
            }
        }

        setErrors(errs)
        return Object.keys(errs).length === 0
    }

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, files } = e.target

        if (name === "profilePhoto") {
            if (files.length > 0) {
                const file = files[0]
                setFormState({ ...formState, profilePhoto: file })
                setPreviewUrl(URL.createObjectURL(file))
                setErrors({ ...errors, profilePhoto: null })
            }
        } else {
            setFormState({ ...formState, [name]: value })
            setErrors({ ...errors, [name]: null })
        }
    }

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validate()) return

        try {
            setLoading(true)
            const formData = new FormData()
            formData.append("name", formState.name)
            formData.append("description", formState.description || "")

            if (formState.profilePhoto) {
                formData.append("file", formState.profilePhoto)
            }

            if (editId) {
                formData.append("coachId", editId)
                await UpdateCoach(formData)
            } else {
                await createCoach(formData)
            }

            await fetchCoaches()
            toggleModal()
        } catch (error) {
            console.error("Error submitting form:", error)
            alert("Failed to save coach. Please try again.")
        } finally {
            setLoading(false)
        }
    }

    // Show delete confirmation
    const showDeleteConfirmation = (coach) => {
        setCoachToDelete(coach)
        setDeleteModalOpen(true)
    }

    // Delete coach
    const handleDelete = async () => {
        if (!coachToDelete) return

        try {
            setDeleteLoading(true)
            await deleteCoach(coachToDelete._id)
            await fetchCoaches()
            toggleDeleteModal()
        } catch (error) {
            console.error("Delete failed:", error)
            alert("Failed to delete coach.")
        } finally {
            setDeleteLoading(false)
        }
    }

    // Edit coach
    const handleEdit = (coach) => {
        setFormState({
            name: coach.name,
            description: coach.description || "",
            profilePhoto: null,
        })
        setPreviewUrl(File_BASE_URL + coach.profilePhoto || null)
        setEditId(coach._id)
        setErrors({})
        setModalOpen(true)
    }

    return (
        <Container fluid className="coach-container py-4">
            <Breadcrumbs mainTitle="Coach" parent="Dashboard" title="Coach" />

            {/* Header Section */}
            <div className="d-flex justify-content-end mb-4">
                <Col xs={12} md={6} className="text-md-end">
                    <CustomButton variant="primary" size="lg" onClick={toggleModal} disabled={loading}>
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Coach
                    </CustomButton>
                </Col>
            </div>

            {/* Loading Spinner */}
            {loading && (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">Loading...</p>
                </div>
            )}

            {/* Coaches Grid */}
            <Row className="coaches-grid">
                {!loading && coaches.length === 0 && (
                    <Col xs={12}>
                        <div className="empty-state text-center py-5">
                            <i className="bi bi-person-plus display-1 text-muted mb-3"></i>
                            <h4 className="text-muted">No coaches found</h4>
                            <p className="text-muted">Get started by adding your first coach</p>
                            <CustomButton variant="primary" size="lg" onClick={toggleModal} className="mt-3">
                                <i className="bi bi-plus-circle me-2"></i>
                                Add First Coach
                            </CustomButton>
                        </div>
                    </Col>
                )}

                {coaches.map((coach) => (
                    <Col xl={3} lg={4} md={6} sm={6} xs={12} className="mb-4" key={coach._id}>
                        <Card className="coach-card h-100">
                            <div className="coach-img-container">
                                {coach.profilePhoto ? (
                                    <CardImg src={File_BASE_URL + coach.profilePhoto} alt={coach.name} className="coach-img" />
                                ) : (
                                    <div className="coach-img-placeholder">
                                        <i className="bi bi-person-circle"></i>
                                    </div>
                                )}
                            </div>

                            <CardBody className="coach-card-body d-flex flex-column">
                                <div className="coach-content flex-grow-1">
                                    <CardTitle tag="h5" className="coach-name">
                                        {coach.name}
                                    </CardTitle>
                                    <CardText className="coach-description">{coach.description || "No description available."}</CardText>
                                </div>

                                <div className="coach-actions mt-auto pt-3">
                                    <div className="d-flex gap-2 justify-content-center">
                                        <CustomButton variant="success" size="sm" onClick={() => handleEdit(coach)} className="flex-fill">
                                            <i className="bi bi-pencil-square me-1"></i>
                                            Edit
                                        </CustomButton>
                                        <CustomButton
                                            variant="danger"
                                            size="sm"
                                            onClick={() => showDeleteConfirmation(coach)}
                                            className="flex-fill"
                                        >
                                            <i className="bi bi-trash me-1"></i>
                                            Delete
                                        </CustomButton>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Modal for Add/Edit */}
            <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md" className="coach-modal">
                <ModalHeader toggle={toggleModal} className="coach-modal-header">
                    <i className={`bi ${editId ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
                    {editId ? "Edit Coach" : "Add New Coach"}
                </ModalHeader>

                <ModalBody className="coach-modal-body">
                    <Form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
                        <FormGroup className="mb-3">
                            <Label for="name" className="form-label">
                                <i className="bi bi-person me-2"></i>
                                Name <span className="text-danger">*</span>
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formState.name}
                                onChange={handleChange}
                                invalid={!!errors.name}
                                placeholder="Enter coach name"
                                className="form-control-modern"
                                required
                            />
                            <FormFeedback>{errors.name}</FormFeedback>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="description" className="form-label">
                                <i className="bi bi-card-text me-2"></i>
                                Description
                            </Label>
                            <Input
                                id="description"
                                name="description"
                                type="textarea"
                                rows="3"
                                value={formState.description}
                                onChange={handleChange}
                                placeholder="Enter coach description (optional)"
                                className="form-control-modern"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="profilePhoto" className="form-label">
                                <i className="bi bi-image me-2"></i>
                                Profile Photo {!editId && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="file"
                                name="profilePhoto"
                                id="profilePhoto"
                                accept=".jpg, .jpeg, .png"
                                onChange={handleChange}
                                invalid={!!errors.profilePhoto}
                                className="form-control-modern"
                            />
                            <FormFeedback>{errors.profilePhoto}</FormFeedback>

                            {previewUrl && (
                                <div className="preview-container mt-3">
                                    <img src={previewUrl || "/placeholder.svg"} alt="Preview" className="preview-image" />
                                </div>
                            )}
                        </FormGroup>
                    </Form>
                </ModalBody>

                <div className="coach-modal-footer">
                    <CustomButton variant="secondary" onClick={toggleModal} disabled={loading}>
                        <i className="bi bi-x-circle me-2"></i>
                        Cancel
                    </CustomButton>
                    <CustomButton variant="primary" onClick={handleSubmit} loading={loading}>
                        <i className={`bi ${editId ? "bi-check-circle" : "bi-plus-circle"} me-2`}></i>
                        {editId ? "Update Coach" : "Create Coach"}
                    </CustomButton>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal} centered size="sm" className="delete-modal">
                <ModalHeader toggle={toggleDeleteModal} className="delete-modal-header">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Confirm Delete
                </ModalHeader>
                <ModalBody className="delete-modal-body text-center">
                    {coachToDelete && (
                        <>
                            <div className="delete-icon mb-3">
                                <i className="bi bi-trash3-fill"></i>
                            </div>
                            <h5 className="mb-3">Delete Coach?</h5>
                            <p className="text-muted mb-0">
                                Are you sure you want to delete <strong>{coachToDelete.name}</strong>? This action cannot be undone.
                            </p>
                        </>
                    )}
                </ModalBody>
                <div className="delete-modal-footer">
                    <CustomButton variant="outline" onClick={toggleDeleteModal} disabled={deleteLoading}>
                        <i className="bi bi-x-circle me-2"></i>
                        Cancel
                    </CustomButton>
                    <CustomButton variant="danger" onClick={handleDelete} loading={deleteLoading}>
                        <i className="bi bi-trash me-2"></i>
                        Delete Coach
                    </CustomButton>
                </div>
            </Modal>
        </Container>
    )
}

export default Coach
